// src/Dashboard/pages/user/Messaging/components/ComposeGroupMessageDrawer.jsx

import React, { useEffect, useMemo, useRef, useState } from "react";

import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import { FaPaperPlane, FaTimes, FaUsers } from "react-icons/fa";

import { toast } from "react-toastify";

import { getAllGroupsMessage } from "../../../../../services/groupServices";

import { MESSAGE_SUBJECT_OPTIONS } from "../utils/messagingUtils";

/* ==========================================================================
   QUILL
   ========================================================================== */

const quillModules = {
  toolbar: [
    [
      {
        header: [2, 3, false],
      },
    ],

    ["bold", "italic", "underline"],

    [
      {
        list: "ordered",
      },
      {
        list: "bullet",
      },
    ],

    ["link", "clean"],
  ],
};

const quillFormats = [
  "header",
  "bold",
  "italic",
  "underline",
  "list",
  "bullet",
  "link",
];

/* ==========================================================================
   HELPERS
   ========================================================================== */

/**
 * Permet de vérifier qu'un contenu Quill contient réellement du texte.
 *
 * Exemple :
 * <p><br></p>
 * ne doit pas être considéré comme un vrai message.
 */
function hasQuillContent(html = "") {
  const plainText = String(html)
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();

  return plainText.length > 0;
}

/* ==========================================================================
   COMPONENT
   ========================================================================== */

export default function ComposeGroupMessageDrawer({ isOpen, onClose, onSend }) {
  /* --------------------------------------------------------------------------
     DATA
     -------------------------------------------------------------------------- */

  const [groups, setGroups] = useState([]);

  const [loadingGroups, setLoadingGroups] = useState(false);

  const [sending, setSending] = useState(false);

  /* --------------------------------------------------------------------------
     FORMULAIRE
     -------------------------------------------------------------------------- */

  const [groupId, setGroupId] = useState("");

  const [subjectChoice, setSubjectChoice] = useState("");

  const [customSubject, setCustomSubject] = useState("");

  const [content, setContent] = useState("");

  /* --------------------------------------------------------------------------
     REFS
     -------------------------------------------------------------------------- */

  /*
   * On conserve toujours la dernière version de onClose,
   * sans provoquer une réinitialisation du formulaire
   * lorsque la référence de la fonction change.
   */
  const onCloseRef = useRef(onClose);

  /*
   * Permet de détecter le passage réel :
   * fermé -> ouvert
   */
  const previousIsOpenRef = useRef(false);

  /* ==========================================================================
     SYNCHRONISATION ONCLOSE
     ========================================================================== */

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  /* ==========================================================================
     RESET DU FORMULAIRE

     IMPORTANT :
     On ne réinitialise QUE lors du passage :
     false -> true

     Ainsi un rerender du parent ne peut plus vider groupId.
     ========================================================================== */

  useEffect(() => {
    const wasOpen = previousIsOpenRef.current;

    /*
     * Le drawer vient réellement
     * de s'ouvrir.
     */
    if (isOpen && !wasOpen) {
      setGroupId("");
      setSubjectChoice("");
      setCustomSubject("");
      setContent("");
      setSending(false);
    }

    previousIsOpenRef.current = isOpen;
  }, [isOpen]);

  /* ==========================================================================
     SCROLL + ESCAPE
     ========================================================================== */

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape" && !sending) {
        onCloseRef.current?.();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;

      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, sending]);

  /* ==========================================================================
     GROUPES
     ========================================================================== */

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    let cancelled = false;

    const loadGroups = async () => {
      try {
        setLoadingGroups(true);

        const data = await getAllGroupsMessage();

        if (!cancelled) {
          setGroups(Array.isArray(data) ? data : []);
        }
      } catch (error) {
        console.error("Erreur chargement groupes message :", error);

        if (!cancelled) {
          toast.error("Erreur lors de la récupération des groupes.");
        }
      } finally {
        if (!cancelled) {
          setLoadingGroups(false);
        }
      }
    };

    loadGroups();

    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  /* ==========================================================================
     OBJET FINAL
     ========================================================================== */

  const finalSubject = useMemo(() => {
    if (subjectChoice === "Autre (saisir)") {
      return customSubject.trim();
    }

    return subjectChoice.trim();
  }, [customSubject, subjectChoice]);

  /* ==========================================================================
     GROUPE SÉLECTIONNÉ

     Utile notamment pour afficher une petite confirmation visuelle.
     ========================================================================== */

  const selectedGroup = useMemo(() => {
    return groups.find((group) => String(group.id) === String(groupId));
  }, [groups, groupId]);

  /* ==========================================================================
     VALIDATION
     ========================================================================== */

  const canSubmit =
    Boolean(groupId) && Boolean(finalSubject) && hasQuillContent(content);

  /* ==========================================================================
     SUBMIT
     ========================================================================== */

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!groupId) {
      toast.error("Veuillez sélectionner un groupe.");

      return;
    }

    if (!finalSubject) {
      toast.error("Veuillez renseigner l’objet du message.");

      return;
    }

    if (!hasQuillContent(content)) {
      toast.error("Veuillez rédiger votre message.");

      return;
    }

    try {
      setSending(true);

      await onSend?.({
        /*
         * On garde volontairement la valeur telle quelle.
         *
         * Si ton backend exige un INTEGER :
         * group_id: Number(groupId)
         *
         * Mais pour ton bug actuel ce n'est pas nécessaire.
         */
        group_id: groupId,

        subject: finalSubject,

        content,
      });
    } catch (error) {
      /*
       * Je ne mets pas de toast générique ici si ton parent
       * gère déjà l'erreur dans onSend.
       */
      console.error("Erreur envoi message groupé :", error);
    } finally {
      setSending(false);
    }
  };

  /* ==========================================================================
     FERMETURE
     ========================================================================== */

  const handleClose = () => {
    if (sending) {
      return;
    }

    onCloseRef.current?.();
  };

  /* ==========================================================================
     RENDER
     ========================================================================== */

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[120]">
      {/* ====================================================================
          OVERLAY
          ==================================================================== */}

      <div
        className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm"
        onClick={handleClose}
        aria-hidden
      />

      {/* ====================================================================
          DRAWER
          ==================================================================== */}

      <aside className="absolute inset-y-0 right-0 flex w-full max-w-4xl flex-col overflow-hidden bg-white shadow-2xl dark:bg-slate-900">
        {/* Barre graphique */}

        <div className="h-1.5 shrink-0 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

        {/* ==================================================================
            HEADER
            ================================================================== */}

        <header className="flex shrink-0 items-center justify-between gap-3 border-b border-slate-200 px-4 py-4 dark:border-slate-800 sm:px-6">
          <div className="min-w-0">
            <p className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">
              <FaUsers />
              Message groupé
            </p>

            <h2 className="truncate text-lg font-bold text-slate-900 dark:text-white sm:text-xl">
              Envoyer un message à un groupe
            </h2>
          </div>

          <button
            type="button"
            onClick={handleClose}
            disabled={sending}
            className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-slate-100 text-slate-700 transition hover:bg-slate-200 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
            aria-label="Fermer"
          >
            <FaTimes />
          </button>
        </header>

        {/* ==================================================================
            FORMULAIRE
            ================================================================== */}

        <form
          onSubmit={handleSubmit}
          className="flex min-h-0 flex-1 flex-col overflow-hidden"
        >
          <div className="flex-1 overflow-y-auto p-4 sm:p-6">
            <div className="space-y-5">
              {/* ============================================================
                  GROUPE
                  ============================================================ */}

              <div>
                <label className="mb-1.5 block text-sm font-bold text-slate-700 dark:text-slate-200">
                  Groupe
                </label>

                {loadingGroups ? (
                  <div className="h-12 animate-pulse rounded-2xl bg-slate-200 dark:bg-slate-800" />
                ) : (
                  <>
                    <select
                      value={groupId}
                      onChange={(event) => {
                        /*
                         * On force une string afin que le value du select
                         * corresponde toujours aux <option>.
                         */
                        setGroupId(String(event.target.value));
                      }}
                      className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-slate-900 outline-none transition focus:border-indigo-400 focus:ring-2 focus:ring-indigo-400/30 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                    >
                      <option value="">Sélectionner un groupe</option>

                      {groups.map((group) => (
                        <option key={group.id} value={String(group.id)}>
                          {group.nom}
                        </option>
                      ))}
                    </select>

                    {selectedGroup && (
                      <div className="mt-2 inline-flex items-center gap-2 rounded-xl bg-emerald-50 px-3 py-2 text-xs font-bold text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300">
                        <FaUsers />
                        Groupe sélectionné : {selectedGroup.nom}
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* ============================================================
                  OBJET
                  ============================================================ */}

              <div
                className={
                  subjectChoice === "Autre (saisir)"
                    ? "grid gap-4 lg:grid-cols-[280px_minmax(0,1fr)]"
                    : "grid gap-4 lg:grid-cols-[minmax(0,420px)]"
                }
              >
                <div>
                  <label className="mb-1.5 block text-sm font-bold text-slate-700 dark:text-slate-200">
                    Objet
                  </label>

                  <select
                    value={subjectChoice}
                    onChange={(event) => {
                      const value = event.target.value;

                      setSubjectChoice(value);

                      if (value !== "Autre (saisir)") {
                        setCustomSubject("");
                      }
                    }}
                    className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-slate-900 outline-none transition focus:border-indigo-400 focus:ring-2 focus:ring-indigo-400/30 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                  >
                    <option value="">Sélectionner un objet</option>

                    {MESSAGE_SUBJECT_OPTIONS.map((subject) => (
                      <option key={subject} value={subject}>
                        {subject}
                      </option>
                    ))}
                  </select>
                </div>

                {subjectChoice === "Autre (saisir)" && (
                  <div>
                    <label className="mb-1.5 block text-sm font-bold text-slate-700 dark:text-slate-200">
                      Objet personnalisé
                    </label>

                    <input
                      type="text"
                      value={customSubject}
                      onChange={(event) => setCustomSubject(event.target.value)}
                      className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-slate-900 outline-none transition focus:border-indigo-400 focus:ring-2 focus:ring-indigo-400/30 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                      placeholder="Ex. : Rappel entraînement..."
                    />
                  </div>
                )}
              </div>

              {/* ============================================================
                  MESSAGE
                  ============================================================ */}

              <div>
                <label className="mb-1.5 block text-sm font-bold text-slate-700 dark:text-slate-200">
                  Message
                </label>

                <div className="overflow-hidden rounded-2xl border border-slate-300 bg-white dark:border-slate-700 dark:bg-slate-950">
                  <ReactQuill
                    theme="snow"
                    value={content}
                    onChange={setContent}
                    modules={quillModules}
                    formats={quillFormats}
                    placeholder="Rédigez votre message groupé..."
                  />
                </div>
              </div>
            </div>
          </div>

          {/* ==================================================================
              FOOTER
              ================================================================== */}

          <footer className="shrink-0 border-t border-slate-200 bg-white/95 p-4 backdrop-blur dark:border-slate-800 dark:bg-slate-900/95">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              {/* Résumé */}

              <div className="min-w-0 text-xs font-semibold text-slate-500 dark:text-slate-400">
                {selectedGroup ? (
                  <>
                    Destinataire :{" "}
                    <span className="font-bold text-slate-700 dark:text-slate-200">
                      {selectedGroup.nom}
                    </span>
                  </>
                ) : (
                  "Sélectionnez un groupe pour continuer."
                )}
              </div>

              {/* Actions */}

              <div className="flex flex-col gap-2 sm:flex-row">
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={sending}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                >
                  <FaTimes />
                  Annuler
                </button>

                <button
                  type="submit"
                  disabled={sending || !canSubmit}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-5 py-3 text-sm font-bold text-white transition hover:bg-indigo-800 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <FaPaperPlane />

                  {sending ? "Envoi..." : "Envoyer au groupe"}
                </button>
              </div>
            </div>
          </footer>
        </form>
      </aside>
    </div>
  );
}
